import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { enterpriseChartTheme, getChartColor, formatChartValue } from "./chartTheme";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      style={{
        background: enterpriseChartTheme.tooltip.backgroundColor,
        border: enterpriseChartTheme.tooltip.border,
        borderRadius: enterpriseChartTheme.tooltip.borderRadius,
        boxShadow: enterpriseChartTheme.tooltip.boxShadow,
        padding: "10px 12px",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>{label}</div>
      {payload.map((entry, index) => (
        <div key={`${entry.name || "value"}-${index}`} style={{ fontSize: 12, color: "#334155", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color || getChartColor(index) }} />
          <span>{entry.name || "Value"}</span>
          <strong style={{ marginLeft: 6 }}>{formatChartValue(entry.value)}</strong>
        </div>
      ))}
    </div>
  );
};

const ChartRenderer = ({ data = [], chartType = "bar", xKey = "name", yKey = "value", colors = [] }) => {
  if (!data.length) {
    return (
      <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 14 }}>
        No data available
      </div>
    );
  }

  const baseProps = {
    data,
    margin: { top: 10, right: 8, left: 0, bottom: 10 },
  };

  const commonAxisProps = {
    tickLine: false,
    axisLine: false,
    tick: { fill: enterpriseChartTheme.axis.tick.fill, fontSize: enterpriseChartTheme.axis.tick.fontSize },
  };

  if (chartType === "line") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <LineChart {...baseProps}>
          <CartesianGrid vertical={false} stroke={enterpriseChartTheme.grid.stroke} strokeDasharray={enterpriseChartTheme.grid.strokeDasharray} />
          <XAxis dataKey={xKey} {...commonAxisProps} />
          <YAxis {...commonAxisProps} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={enterpriseChartTheme.legend.wrapperStyle} />
          <Line type="monotone" dataKey={yKey} stroke={colors[0] || enterpriseChartTheme.colors[0]} strokeWidth={2.6} dot={{ r: 3, fill: colors[0] || enterpriseChartTheme.colors[0] }} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "area") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart {...baseProps}>
          <CartesianGrid vertical={false} stroke={enterpriseChartTheme.grid.stroke} strokeDasharray={enterpriseChartTheme.grid.strokeDasharray} />
          <XAxis dataKey={xKey} {...commonAxisProps} />
          <YAxis {...commonAxisProps} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={enterpriseChartTheme.legend.wrapperStyle} />
          <Area type="monotone" dataKey={yKey} stroke={colors[0] || enterpriseChartTheme.colors[0]} fill={colors[0] || enterpriseChartTheme.colors[0]} fillOpacity={0.2} strokeWidth={2.6} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "pie") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={enterpriseChartTheme.legend.wrapperStyle} />
          <Pie data={data} dataKey={yKey} nameKey={xKey} innerRadius={50} outerRadius={82} paddingAngle={2}>
            {data.map((entry, index) => (
              <Cell key={`${entry[xKey]}-${index}`} fill={colors[index] || getChartColor(index)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "doughnut") {
    return (
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={enterpriseChartTheme.legend.wrapperStyle} />
          <Pie data={data} dataKey={yKey} nameKey={xKey} innerRadius={70} outerRadius={90} paddingAngle={2}>
            {data.map((entry, index) => (
              <Cell key={`${entry[xKey]}-${index}`} fill={colors[index] || getChartColor(index)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart {...baseProps}>
        <CartesianGrid vertical={false} stroke={enterpriseChartTheme.grid.stroke} strokeDasharray={enterpriseChartTheme.grid.strokeDasharray} />
        <XAxis dataKey={xKey} {...commonAxisProps} />
        <YAxis {...commonAxisProps} />
        <Tooltip content={<CustomTooltip />} />
        <Legend wrapperStyle={enterpriseChartTheme.legend.wrapperStyle} />
        <Bar dataKey={yKey} radius={[6, 6, 0, 0]} fill={colors[0] || enterpriseChartTheme.colors[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartRenderer;
